import { createSelector } from '@ngrx/store';
import { selectEntities } from '../entities';
import { UserAnnouncementEntitiesState } from './user-announcement-entities.state';

/**
 * Selectors für die Endnutzer-Announcements.
 */
export class UserAnnouncementEntitiesSelectors {
  private static readonly _feature = createSelector(
    selectEntities,
    (state): UserAnnouncementEntitiesState => state.userAnnouncementEntities,
  );

  /** Alle geladenen Announcements. */
  static readonly announcements = createSelector(
    this._feature,
    (state) => state.announcements,
  );

  /** Aktuell angezeigtes Announcement basierend auf currentIndex. */
  static readonly currentAnnouncement = createSelector(
    this._feature,
    (state) => state.announcements[state.currentIndex] ?? null,
  );

  /** Aktueller Navigationsindex. */
  static readonly currentIndex = createSelector(
    this._feature,
    (state) => state.currentIndex,
  );

  /** Gesamtanzahl der Announcements. */
  static readonly totalCount = createSelector(
    this._feature,
    (state) => state.announcements.length,
  );

  /** Ladezustand. */
  static readonly isLoading = createSelector(
    this._feature,
    (state) => state.isLoading,
  );

  /** Gibt an, ob die Announcements mindestens einmal geladen wurden. */
  static readonly isLoaded = createSelector(
    this._feature,
    (state) => state.isLoaded,
  );

  /** Ob Announcement-Pop-Ups für den Nutzer aktiviert sind. */
  static readonly enabled = createSelector(
    this._feature,
    (state) => state.enabled,
  );

  /** Ob ungelesene Announcements vorhanden sind (für Glocken-Icon). */
  static readonly hasUnread = createSelector(
    this._feature,
    (state) => state.hasUnread,
  );

  /** Fehlermeldung. */
  static readonly error = createSelector(
    this._feature,
    (state) => state.error,
  );

  /** Ob Navigation zum nächsten Eintrag möglich ist. */
  static readonly hasNext = createSelector(
    this._feature,
    (state) => state.currentIndex < state.announcements.length - 1,
  );

  /** Ob Navigation zum vorherigen Eintrag möglich ist. */
  static readonly hasPrev = createSelector(
    this._feature,
    (state) => state.currentIndex > 0,
  );

  /** Ob die Navigations-Buttons angezeigt werden sollen (> 1 Announcement). */
  static readonly navigationVisible = createSelector(
    this._feature,
    (state) => state.announcements.length > 1,
  );

  /** Alle Blob-URLs als Record. */
  static readonly imageUrls = createSelector(
    this._feature,
    (state) => state.imageUrls,
  );

  /** IDs der als gelesen markierten Announcements. */
  static readonly markedReadIds = createSelector(
    this._feature,
    (state) => state.markedReadIds,
  );

  /**
   * Blob-URL eines Bildes per imageId.
   * @param imageId - Die ID des Bildes
   */
  static imageUrl(imageId: string) {
    return createSelector(
      this._feature,
      (state) => state.imageUrls[imageId] ?? null,
    );
  }

  /** Blob-URL des Bildes des aktuell angezeigten Announcements. */
  static readonly currentAnnouncementImageUrl = createSelector(
    this._feature,
    (state) => {
      const announcement = state.announcements[state.currentIndex];
      if (!announcement?.imageId) {
        return null;
      }
      return state.imageUrls[announcement.imageId] ?? null;
    },
  );
}
